import { FC, useEffect, useRef } from 'react'
import { toAbsoluteUrl } from '../../../../_metronic/helpers'
import { Button, ContactSupportLink, InfoAlert } from '../../../components'

type IProps = {
  /** Erro capturado pelo ErrorBoundary; a mensagem só aparece fora de produção. */
  error?: unknown
  /** Id do evento no Sentry: é o "código do erro" que o usuário passa pro suporte. */
  eventId?: string | null
  /** Quando existe, mostra "Tentar novamente", que remonta a tela sem recarregar. */
  onTentarNovamente?: () => void
  /** `pagina` ocupa a viewport inteira (fora do layout); `conteudo` cabe na área de conteúdo. */
  variante?: 'pagina' | 'conteudo'
}

const mensagemDoErro = (error: unknown): string | undefined => {
  if (process.env.NODE_ENV === 'production') return undefined
  if (error instanceof Error) return `${error.name}: ${error.message}`
  return error ? String(error) : undefined
}

/**
 * Tela de erro inesperado (fallback dos ErrorBoundary). O botão de início é um link comum, não
 * `<Link>`, de propósito: o boundary da raiz fica fora do Router, e depois de um crash a
 * navegação completa é o que garante estado limpo.
 */
export const ErroInesperado: FC<IProps> = ({
  error,
  eventId,
  onTentarNovamente,
  variante = 'pagina',
}) => {
  const detalhe = mensagemDoErro(error)
  const tituloRef = useRef<HTMLHeadingElement>(null)

  // O elemento que tinha o foco sumiu com a tela que quebrou; o título recebe o foco para o
  // teclado e o leitor de tela recomeçarem daqui, e não do início do documento.
  useEffect(() => {
    tituloRef.current?.focus()
  }, [])

  return (
    <div
      className={
        variante === 'pagina'
          ? 'd-flex flex-column flex-root'
          : 'd-flex flex-column flex-column-fluid w-100'
      }
    >
      <div className='d-flex flex-column flex-column-fluid text-center p-10 py-lg-15'>
        <div className='pt-lg-10 mb-10'>
          <div role='alert'>
            <h1 ref={tituloRef} tabIndex={-1} className='fw-bolder fs-2qx text-gray-800 mb-10'>
              Algo deu errado
            </h1>
            <div className='fw-bold fs-3 text-muted mb-10'>
              A tela encontrou um erro inesperado e não pôde continuar.
              <br />
              Volte para a página inicial ou tente novamente.
            </div>
          </div>

          {eventId && (
            <p className='fs-6 text-gray-700 mb-10'>
              Código do erro:{' '}
              <code className='user-select-all text-danger bg-transparent'>{eventId}</code>
              <br />
              <ContactSupportLink
                message='Se precisar, informe esse código ao suporte.'
                className='link-primary fs-6 d-inline-block py-2'
              />
            </p>
          )}

          {detalhe && (
            <InfoAlert
              variant='danger'
              title='Detalhe do erro (só fora de produção)'
              className='text-start mx-auto mw-800px mb-10'
            >
              <pre className='fs-7 text-gray-800 mb-0 text-wrap'>{detalhe}</pre>
            </InfoAlert>
          )}

          <div className='d-flex justify-content-center gap-4 flex-wrap'>
            {/* Mesma altura do <Button variant> ao lado (40px). */}
            <a href='/' className='btn btn-primary fw-bolder h-40px d-flex align-items-center'>
              Ir para a página inicial
            </a>
            {onTentarNovamente && (
              <Button variant='secondary-outline' onClick={onTentarNovamente}>
                Tentar novamente
              </Button>
            )}
          </div>
        </div>

        <div
          className='d-flex flex-row-auto bgi-no-repeat bgi-position-x-center bgi-size-contain bgi-position-y-bottom min-h-100px min-h-lg-350px'
          style={{
            backgroundImage: `url('${toAbsoluteUrl('/media/illustrations/sketchy-1/17.png')}')`,
          }}
        ></div>
      </div>
    </div>
  )
}
